import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

function modeLabel(mode) {
  if (mode === 'local') return 'Allow to Record Locally';
  if (mode === 'both') return 'Both (local + ERP4C Cloud/Server)';
  return 'Allow to Record on the ERP4C Cloud/Server';
}

/**
 * Training Administration → Jitsi Settings → Recording Settings
 */
export default function TrainingJitsiSettingsPanel() {
  const [innerTab, setInnerTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaultMode, setDefaultMode] = useState('cloud');
  const [instructors, setInstructors] = useState([]);
  const [modes, setModes] = useState([]);
  const [deploy, setDeploy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/admin/jitsi-recording-settings');
      const data = r.data?.data || {};
      setDefaultMode(
        data.default_mode === 'local' || data.default_mode === 'both'
          ? data.default_mode
          : 'cloud'
      );
      setInstructors(data.instructors || []);
      setModes(data.modes || []);
      setDeploy(data.deploy || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load Jitsi recording settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveDefault = async () => {
    setSaving(true);
    try {
      await api.put('/training/admin/jitsi-recording-settings', { default_mode: defaultMode });
      toast.success('System recording mode saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const setInstructorMode = async (userId, recording_mode) => {
    try {
      await api.put(`/training/admin/jitsi-recording-settings/instructors/${userId}`, {
        recording_mode,
      });
      toast.success('Instructor override updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update instructor');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const defaultModes =
    modes.length > 0
      ? modes
      : [
          { id: 'local', label: 'Allow to Record Locally', description: '' },
          { id: 'cloud', label: 'Allow to Record on the ERP4C Cloud/Server', description: '' },
          { id: 'both', label: 'Both (local and ERP4C Cloud/Server)', description: '' },
        ];

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Jitsi Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Control how instructors may record Live Join Video Call sessions. Learners never get a
        recording control.
      </Typography>

      <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)} sx={{ mb: 2 }}>
        <Tab label="Recording Settings" />
      </Tabs>

      {innerTab === 0 && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              System default (all instructors)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Used when an instructor has no override below.
            </Typography>
            <RadioGroup value={defaultMode} onChange={(e) => setDefaultMode(e.target.value)}>
              {defaultModes.map((m) => (
                <FormControlLabel
                  key={m.id}
                  value={m.id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography fontWeight={600}>{m.label}</Typography>
                      {m.description ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {m.description}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
              ))}
            </RadioGroup>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={saveDefault}
              sx={{ mt: 1 }}
            >
              {saving ? 'Saving…' : 'Save system default'}
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              Override for specific instructor
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Choose a mode for one instructor, or Inherit to use the system default.
            </Typography>
            {instructors.length === 0 ? (
              <Alert severity="info">
                No course instructors found yet. Assign instructors on a course under Manage Training.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Instructor</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Effective</TableCell>
                      <TableCell width={280}>Override</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {instructors.map((ins) => (
                      <TableRow key={ins.user_id}>
                        <TableCell>
                          {[ins.first_name, ins.last_name].filter(Boolean).join(' ') || '—'}
                        </TableCell>
                        <TableCell>{ins.email}</TableCell>
                        <TableCell>
                          {modeLabel(ins.effective_mode)}
                          {ins.is_custom ? ' (override)' : ' (default)'}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <InputLabel id={`rec-${ins.user_id}`}>Mode</InputLabel>
                            <Select
                              labelId={`rec-${ins.user_id}`}
                              label="Mode"
                              value={ins.is_custom ? ins.recording_mode : 'inherit'}
                              onChange={(e) => {
                                const v = e.target.value;
                                setInstructorMode(ins.user_id, v === 'inherit' ? 'inherit' : v);
                              }}
                            >
                              <MenuItem value="inherit">Inherit system default</MenuItem>
                              <MenuItem value="local">Allow to Record Locally</MenuItem>
                              <MenuItem value="cloud">
                                Allow to Record on the ERP4C Cloud/Server
                              </MenuItem>
                              <MenuItem value="both">
                                Both (local + ERP4C Cloud/Server)
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              Deploy parameters (local Docker Desktop / Azure)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              These come from Helm / pod env (read-only here). Change via values for local or Azure
              and redeploy.
            </Typography>
            <Stack spacing={0.75} sx={{ fontFamily: 'monospace', fontSize: 13, mb: 1.5 }}>
              <Box>
                Browser Meet URL (publicUrl):{' '}
                <strong>{deploy?.browser_public_url || '—'}</strong>
              </Box>
              <Box>
                Jibri join URL (jitsi.jibri.joinPublicUrl):{' '}
                <strong>{deploy?.jibri_join_url || '(auto in-cluster jitsi-web:80)'}</strong>
              </Box>
              <Box>
                JITSI_DOMAIN: <strong>{deploy?.jitsi_domain || '—'}</strong>
              </Box>
            </Stack>
            {(deploy?.notes || []).map((n) => (
              <Typography key={n.slice(0, 40)} variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                • {n}
              </Typography>
            ))}
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
